import { useMutation, useQueryClient } from '@tanstack/react-query';
import { getApiClient } from '@signalvote/api-client';
import { createTaskRequest, updateTaskRequest } from '@signalvote/shared';
import type { CreateTaskRequest, UpdateTaskRequest, TaskListResponse, TaskListItemDto } from '@signalvote/shared';
import type { TaskListParams } from '@/lib/queryKeys';
import { defineQuery, requireSelectedCampaign, selectedCampaignKey } from '@/lib/data';
import { useAuth } from '@/contexts/AuthContext';

const api = getApiClient();

/** Every task list's key prefix under the selected campaign, for the optimistic edits. */
const taskListsKey = () => selectedCampaignKey(['tasks', 'list']);

/**
 * Tasks with optional filters. Every task read declares its GET's reads, so a write naming a task,
 * its category or a volunteer refreshes it with no key listed.
 */
export const useTasks = defineQuery({
  key: (params: TaskListParams | undefined) => ['tasks', 'list', params],
  reads: 'GET /api/Tasks',
  fetch: (params, { campaignId }): Promise<TaskListResponse> => {
    requireSelectedCampaign(campaignId);
    return api.tasks.getTasks(params);
  },
});

type NewTask = Pick<CreateTaskRequest, 'title'> &
  Partial<CreateTaskRequest> & { assignedToName?: string; categoryName?: string };

/**
 * Create a new task with optimistic updates.
 * Accepts optional `assignedToName` for display in the optimistic entry.
 */
export function useCreateTask() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: (data: NewTask) => {
      // Strip display-only fields before sending to API
      const { assignedToName, categoryName, ...request } = data;
      return api.tasks.createTask(createTaskRequest(request));
    },
    onMutate: async (newTask) => {
      await queryClient.cancelQueries({ queryKey: taskListsKey() });

      const previousQueries = queryClient.getQueriesData<TaskListResponse>({ queryKey: taskListsKey() });

      const request = createTaskRequest(newTask);
      const optimisticItem: TaskListItemDto = {
        id: `temp-${Date.now()}`,
        title: request.title,
        source: request.source,
        status: 'Pending',
        dueDate: request.dueDate,
        dueTime: request.dueTime,
        durationMinutes: request.durationMinutes,
        assignedToId: request.assignedToId,
        assignedToName: newTask.assignedToName ?? null,
        categoryId: request.categoryId,
        categoryName: newTask.categoryName ?? null,
        actionUrl: request.actionUrl,
        isOpen: !request.assignedToId,
        acknowledgedAt: null,
        createdById: user?.id ?? null,
        createdByName: user ? `${user.firstName} ${user.lastName}` : null,
      };

      queryClient.setQueriesData<TaskListResponse>(
        { queryKey: taskListsKey() },
        (old) => {
          if (!old || !('items' in old)) return old;
          return {
            ...old,
            items: [optimisticItem, ...old.items],
            totalCount: old.totalCount + 1,
          };
        }
      );

      return { previousQueries };
    },
    onError: (_err, _newTask, context) => {
      context?.previousQueries.forEach(([key, data]) => {
        queryClient.setQueryData(key, data);
      });
    },
  });
}

/**
 * Update an existing task
 */
export function useUpdateTask() {
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<UpdateTaskRequest> }) =>
      api.tasks.updateTask(id, updateTaskRequest(data)),
  });
}

/**
 * Delete a task
 */
export function useDeleteTask() {
  return useMutation({
    mutationFn: (id: string) => api.tasks.deleteTask(id),
  });
}

/**
 * Accept an assigned task
 */
export function useAcceptTask() {
  return useMutation({
    mutationFn: (id: string) => api.tasks.acceptTask(id),
  });
}

/**
 * Decline an assigned task
 */
export function useDeclineTask() {
  return useMutation({
    mutationFn: (id: string) => api.tasks.declineTask(id),
  });
}

/**
 * Complete a task with optimistic cache update for instant sidebar reflection.
 */
export function useCompleteTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.tasks.completeTask(id),
    onMutate: async (taskId) => {
      await queryClient.cancelQueries({ queryKey: taskListsKey() });

      const previousQueries = queryClient.getQueriesData<TaskListResponse>({ queryKey: taskListsKey() });

      queryClient.setQueriesData<TaskListResponse>(
        { queryKey: taskListsKey() },
        (old) => {
          if (!old || !('items' in old)) return old;
          return {
            ...old,
            items: old.items.map((item) =>
              item.id === taskId ? { ...item, status: 'Completed' } : item
            ),
          };
        }
      );

      return { previousQueries };
    },
    onError: (_err, _taskId, context) => {
      context?.previousQueries.forEach(([key, data]) => {
        queryClient.setQueryData(key, data);
      });
    },
  });
}

/**
 * Reopen a completed task back to Pending with optimistic cache update.
 */
export function useReopenTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.tasks.reopenTask(id),
    onMutate: async (taskId) => {
      await queryClient.cancelQueries({ queryKey: taskListsKey() });

      const previousQueries = queryClient.getQueriesData<TaskListResponse>({ queryKey: taskListsKey() });

      queryClient.setQueriesData<TaskListResponse>(
        { queryKey: taskListsKey() },
        (old) => {
          if (!old || !('items' in old)) return old;
          return {
            ...old,
            items: old.items.map((item) =>
              item.id === taskId ? { ...item, status: 'Pending' } : item
            ),
          };
        }
      );

      return { previousQueries };
    },
    onError: (_err, _taskId, context) => {
      context?.previousQueries.forEach(([key, data]) => {
        queryClient.setQueryData(key, data);
      });
    },
  });
}

/**
 * The count of unacknowledged inbound task assignments, for the header badge. It polls because
 * another member's assignment answers their request, not this tab's.
 */
export const useUnacknowledgedTaskCount = defineQuery({
  key: () => ['tasks', 'unacknowledged-count'],
  reads: 'GET /api/Tasks/unacknowledged-count',
  fetch: (_: undefined, { campaignId }) => {
    requireSelectedCampaign(campaignId);
    return api.tasks.getUnacknowledgedCount();
  },
  refetchInterval: 60_000,
});

/**
 * Acknowledge a task (mark as seen by the assignee).
 * Invalidates both the task list and unacknowledged count.
 */
export function useAcknowledgeTask() {
  return useMutation({
    mutationFn: (id: string) => api.tasks.acknowledgeTask(id),
  });
}
